import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Users, UserPlus, Trash2, Crown, User as UserIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import DocumentUpload from "@/components/collaboration/DocumentUpload";
import CommentSection from "@/components/collaboration/CommentSection";

const roleLabels = {
  leader: "Teamleder",
  member: "Medlem",
  contributor: "Bidragsyter",
};

export default function TeamDetail() {
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedRole, setSelectedRole] = useState("member");
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const urlParams = new URLSearchParams(window.location.search);
  const teamId = urlParams.get("id");

  const { data: team, isLoading: loadingTeam } = useQuery({
    queryKey: ["team", teamId],
    queryFn: async () => {
      const teams = await base44.entities.Team.filter({ id: teamId });
      return teams[0];
    },
    enabled: !!teamId,
  });

  const { data: members = [], isLoading: loadingMembers } = useQuery({
    queryKey: ["teamMembers", teamId],
    queryFn: () => base44.entities.TeamMember.filter({ team_id: teamId }),
    enabled: !!teamId,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const addMemberMutation = useMutation({
    mutationFn: (data) => base44.entities.TeamMember.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teamMembers", teamId] });
      setSelectedUser("");
      setSelectedRole("member");
    },
  });

  const updateMemberMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.TeamMember.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teamMembers", teamId] });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (id) => base44.entities.TeamMember.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teamMembers", teamId] });
    },
  });

  const deleteTeamMutation = useMutation({
    mutationFn: (id) => base44.entities.Team.delete(id),
    onSuccess: () => {
      navigate(createPageUrl("Teams"));
    },
  });

  const updateTeamMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Team.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team", teamId] });
      queryClient.invalidateQueries({ queryKey: ["teams"] });
    },
  });

  const handleAddMember = () => {
    if (!selectedUser) return;
    const user = users.find(u => u.email === selectedUser);
    
    addMemberMutation.mutate({
      team_id: teamId,
      team_name: team?.name,
      user_email: user?.email,
      user_name: user?.full_name,
      role: selectedRole,
    });
  };

  const handleRoleChange = (memberId, newRole) => {
    const member = members.find(m => m.id === memberId);
    updateMemberMutation.mutate({
      id: memberId,
      data: { ...member, role: newRole },
    });
  };

  const availableUsers = users.filter(u => 
    !members.some(m => m.user_email === u.email)
  );

  if (loadingTeam) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Laster...</div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Team ikke funnet</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link to={createPageUrl("Teams")}>
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tilbake til team
          </Button>
        </Link>

        {/* Team Header */}
        <Card className="p-6 bg-white border-0 shadow-sm mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="p-4 bg-lime-100 rounded-xl">
                <Users className="w-8 h-8 text-zinc-900" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{team.name}</h1>
                {team.description && (
                  <p className="text-slate-600 mt-1">{team.description}</p>
                )}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <Select 
                    value={team.status} 
                    onValueChange={(status) => updateTeamMutation.mutate({ id: teamId, data: { ...team, status } })}
                  >
                    <SelectTrigger className="w-32 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                          Aktiv
                        </div>
                      </SelectItem>
                      <SelectItem value="inactive">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-slate-400" />
                          Inaktiv
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-slate-500">{members.length} medlemmer</span>
                  {team.related_to_name && (
                    <Badge variant="outline" className="text-xs">
                      → {team.related_to_name}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="icon"
              className="text-rose-600 hover:text-rose-700 border-rose-200 hover:bg-rose-50"
              onClick={() => {
                if (confirm("Er du sikker på at du vil slette dette teamet?")) {
                  deleteTeamMutation.mutate(teamId);
                }
              }}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Members Section */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6 bg-white border-0 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Teammedlemmer</h2>
              
              {/* Add Member Form */}
              <div className="flex gap-2 mb-6">
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Velg bruker..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUsers.map((user) => (
                      <SelectItem key={user.id} value={user.email}>
                        {user.full_name} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="leader">Teamleder</SelectItem>
                    <SelectItem value="member">Medlem</SelectItem>
                    <SelectItem value="contributor">Bidragsyter</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  onClick={handleAddMember}
                  disabled={!selectedUser || addMemberMutation.isPending}
                >
                  <UserPlus className="w-4 h-4" />
                </Button>
              </div>

              {/* Members List */}
              {loadingMembers ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="p-4 bg-slate-50 rounded-lg animate-pulse">
                      <div className="h-4 bg-slate-200 rounded w-2/3" />
                    </div>
                  ))}
                </div>
              ) : members.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">Ingen medlemmer ennå</p>
              ) : (
                <div className="space-y-2">
                  {members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-lime-100 flex items-center justify-center">
                          {member.role === "leader" ? (
                            <Crown className="w-5 h-5 text-zinc-900" />
                          ) : (
                            <UserIcon className="w-5 h-5 text-zinc-900" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{member.user_name}</p>
                          <p className="text-sm text-slate-500">{member.user_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select 
                          value={member.role} 
                          onValueChange={(role) => handleRoleChange(member.id, role)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="leader">Teamleder</SelectItem>
                            <SelectItem value="member">Medlem</SelectItem>
                            <SelectItem value="contributor">Bidragsyter</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeMemberMutation.mutate(member.id)}
                        >
                          <Trash2 className="w-4 h-4 text-slate-400 hover:text-rose-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <CommentSection
              relatedToType="team"
              relatedToId={teamId}
              relatedToName={team.name}
            />
            <DocumentUpload 
              relatedToType="team" 
              relatedToId={teamId} 
              relatedToName={team.name}
            />
          </div>
        </div>
      </div>
    </div>
  );
}